import { EventEmitter } from "@angular/core";
import { AbstractControl, AsyncValidatorFn, FormControl, Validators } from "@angular/forms";
import { Subject, takeUntil, tap } from "rxjs";
import { ApiService } from "../services/api.service";
import { BeforeSubmitArgs } from "./before-submit-args";
import { QQQuestionnairePageViewModel } from "./qq-questionnaire-page.view-model";
import { QuestionModel } from "./question.model";

export class QQQuestionViewModel {
  initialScreen: boolean;
  id: string;
  type: string;
  label: string;
  tip: string;
  key: string;
  helpKey: string;
  condition: Function;
  hasValidations: boolean;
  options: any;
  answer: string | null;
  error: string;
  retryValidation: () => void;
  editingValue: string | null;
  visible = true;
  formControl: FormControl;
  onChange = new EventEmitter<string>();
  beforeSubmit$ = new EventEmitter<BeforeSubmitArgs>();

  constructor(
    public question: QuestionModel,
    private api: ApiService,
    public page: QQQuestionnairePageViewModel
  ) {
    this.id = question.id;
    this.type = question.type;
    this.label = question.label;
    this.key = question.key;
    this.helpKey = question.helpKey;
    this.condition = question.condition ? new Function('context', `with(context) { return ${question.condition}; }`) : null;
    this.hasValidations = question.hasValidations;
    this.options = question.options;
    this.answer = "";
    this.tip = question.tip;
    const asyncValidators: AsyncValidatorFn[] = [];
    if (this.hasValidations)
      asyncValidators.push(this.validator);

    this.formControl = new FormControl(this.answer, [Validators.required], asyncValidators)
  }

  beforeSubmit(dispose$: Subject<any>, callback: (e: BeforeSubmitArgs) => Promise<any> | any) {
    this.beforeSubmit$
      .pipe(
        takeUntil(dispose$),
        tap(callback)
      ).subscribe();
  }

  async setAnswer(value: string, reset: boolean) {
    if (reset)
      this.error = null;
    else if (this.error) {
      this.onChange.emit(null);
      return false;
    }

    if (this.answer === value)
      return true;

    const valid = await this.validate(value);
    if (valid) {
      this.answer = value;
      this.onChange.emit(value);
    }
    else
      this.onChange.emit(null);
    return valid;
  }

  public checkVisibility(context: { [key: string]: string }) {
    this.visible = this.condition ? this.condition(context) : true;
  }

  private async validator(control: AbstractControl) {
    if (await this.validate(control.value))
      return null;
    return [this.error];
  }

  private async validate(value: string) {
    if (!this.question.hasValidations)
      return true;
    // const prevMode = this.mode;
    // this.mode = 'validating';

    const answers = [...this.page.questionnaire.answers]
      .map(([key, value]) => ({ Key: key, Value: value?.toString() }));

    try {
      const rsp = await this.api.validate(this.question.id, value, answers);
      this.retryValidation = null;
      if (!rsp.valid) {
        this.error = rsp.error;
        return false;
      }

      return true;
    }
    catch (err: any) {
      this.error = 'Error validating value';
      this.retryValidation = () => {
        this.error = null;
        this.setAnswer(value, false);
      };
      return false;
    }
    finally {
      // this.mode = prevMode;
    }
  }
}
